import React, { useState } from "react";
import { Box, Typography, Grid, MenuItem, Button } from "@mui/material";
import { CalculatorTextFieldItem } from "../components/CalculatorTextFieldItem";
import CalculatorSelectFieldItem from "../components/CalculatorSelectFieldItem";

export default function PCPTComponent() {
  const [psa, setPsa] = useState<string>("");
  const [pctFreePsa, setPctFreePsa] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [race, setRace] = useState<string>("caucasian");
  const [familyHistory, setFamilyHistory] = useState<string>("no");
  const [dre, setDre] = useState<string>("normal");
  const [priorBiopsy, setPriorBiopsy] = useState<string>("no");
  const [result, setResult] = useState<React.ReactNode | null>(null);

  const calculate = () => {
    const psaVal = parseFloat(psa);
    const ageVal = parseFloat(age);
    let fpsaVal: number | null = null;

    if (Number.isNaN(psaVal) || psaVal < 0.1 || psaVal > 50) {
      setResult(
        <Typography color="error">
          Please enter a valid PSA between 0.1 and 50 ng/mL.
        </Typography>,
      );
      return;
    }

    if (Number.isNaN(ageVal) || ageVal < 50 || ageVal > 90) {
      setResult(
        <Typography color="error">
          Please enter a valid Age between 50 and 90.
        </Typography>,
      );
      return;
    }

    if (pctFreePsa !== "") {
      fpsaVal = parseFloat(pctFreePsa);
      if (Number.isNaN(fpsaVal) || fpsaVal < 0 || fpsaVal > 100) {
        setResult(
          <Typography color="error">
            Please enter a valid % Free PSA between 0 and 100.
          </Typography>,
        );
        return;
      }
    }

    let warningMsg = "";
    if (psaVal > 10) {
      warningMsg =
        " (Note: The PCPTRC 2.0 was optimized for PSA ≤ 10 ng/mL. Probabilities may be less accurate for higher values.)";
    }

    const isAA = race === "aa" ? 1 : 0;
    const hasFamilyHistory = familyHistory === "yes" ? 1 : 0;
    const isDreAbnormal = dre === "abnormal" ? 1 : 0;
    const hasPriorNegativeBiopsy = priorBiopsy === "yes" ? 1 : 0;

    // Coefficients for the multinomial logistic regression model (No, Low, High grade)
    // Variables order: Intercept, log2(PSA), Age, isAA, PriorBiopsy, DRE, FamilyHistory
    const noLow = [
      -3.00215469, 0.2561339, 0.01643637, 0.12172599, -0.45533257, -0.03864628,
      0.27197219,
    ];
    const noHigh = [
      -7.05304534, 0.70489441, 0.04753804, 1.04174529, -0.21409933, 0.40068434,
      0.22467348,
    ];

    const data = [
      1,
      Math.log2(psaVal),
      ageVal,
      isAA,
      hasPriorNegativeBiopsy,
      isDreAbnormal,
      hasFamilyHistory,
    ];

    let s1 = 0;
    let s2 = 0;
    for (let i = 0; i < data.length; i += 1) {
      s1 += noLow[i] * data[i];
      s2 += noHigh[i] * data[i];
    }

    let riskNo = 1 / (1 + Math.exp(s1) + Math.exp(s2));
    let riskLow = Math.exp(s1) / (1 + Math.exp(s1) + Math.exp(s2));
    let riskHigh = 1 - riskNo - riskLow;

    // Apply % Free PSA Likelihood Ratio adjustment if provided
    if (fpsaVal !== null) {
      const dnorm = (x: number, mean: number, sd: number) =>
        Math.exp(-0.5 * ((x - mean) / sd) ** 2) / (sd * Math.sqrt(2 * Math.PI));

      const log2Psa = Math.log2(psaVal);
      const log2Fpsa = Math.log2(fpsaVal);

      const meanHigh = 4.8059009 + -0.3483031 * log2Psa;
      const meanLow = 4.9730353 + -0.3856204 * log2Psa;
      const meanNo = 4.9602101 + -0.2195069 * log2Psa;

      const probUpdateHigh = dnorm(log2Fpsa, meanHigh, 0.6452902);
      const probUpdateLow = dnorm(log2Fpsa, meanLow, 0.6010974);
      const probUpdateNo = dnorm(log2Fpsa, meanNo, 0.5154306);

      const pHighRaw = riskHigh * probUpdateHigh;
      const pLowRaw = riskLow * probUpdateLow;
      const pNoRaw = riskNo * probUpdateNo;

      const sumRaw = pHighRaw + pLowRaw + pNoRaw;

      riskHigh = pHighRaw / sumRaw;
      riskLow = pLowRaw / sumRaw;
      riskNo = pNoRaw / sumRaw;
    }

    const probAny = (1 - riskNo) * 100;
    const probHigh = riskHigh * 100;
    const probLow = riskLow * 100;

    setResult(
      <>
        Risk of Any Prostate Cancer: <b>{probAny.toFixed(1)}%</b> <br />
        Risk of High-Grade Prostate Cancer (Gleason ≥ 7):{" "}
        <b>{probHigh.toFixed(1)}%</b> <br />
        Risk of Low-Grade Prostate Cancer (Gleason ≤ 6):{" "}
        <b>{probLow.toFixed(1)}%</b>
        {warningMsg && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: "block", mt: 1 }}
          >
            {warningMsg}
          </Typography>
        )}
      </>,
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          PCPT Risk Calculator 2.0 estimates the probability of finding prostate
          cancer on biopsy.
        </Typography>

        <Grid container spacing={2}>
          <CalculatorTextFieldItem
            text="PSA (ng/ml)"
            value={psa}
            onChange={(e) => setPsa(e.target.value)}
          />
          <CalculatorTextFieldItem
            text="% Free PSA (Optional)"
            value={pctFreePsa}
            onChange={(e) => setPctFreePsa(e.target.value)}
          />
          <CalculatorTextFieldItem
            text="Age"
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
          <CalculatorSelectFieldItem
            text="Race"
            value={race}
            onChange={(e) => setRace(e.target.value)}
          >
            <MenuItem value="caucasian">Caucasian</MenuItem>
            <MenuItem value="aa">African Descent</MenuItem>
            <MenuItem value="hispanic">Hispanic</MenuItem>
            <MenuItem value="asian">Asian</MenuItem>
            <MenuItem value="native-american">Native American</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorSelectFieldItem
            text="Family History of PCa"
            value={familyHistory}
            onChange={(e) => setFamilyHistory(e.target.value)}
          >
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorSelectFieldItem
            text="DRE Finding"
            value={dre}
            onChange={(e) => setDre(e.target.value)}
          >
            <MenuItem value="normal">Normal / Not Suspicious</MenuItem>
            <MenuItem value="abnormal">Abnormal / Suspicious</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorSelectFieldItem
            text="Prior Negative Biopsy"
            value={priorBiopsy}
            onChange={(e) => setPriorBiopsy(e.target.value)}
          >
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </CalculatorSelectFieldItem>
        </Grid>

        <Button
          variant="contained"
          onClick={calculate}
          sx={{ mt: 2, mb: 2 }}
          fullWidth
        >
          Calculate Risk
        </Button>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="h5">Result</Typography>
        {!result ? (
          <Typography>
            <i>Please fill out the fields and click Calculate</i>
          </Typography>
        ) : (
          <Typography variant="body1" component="div">
            {result}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
